<script setup lang="ts">
    import { ref, watch } from 'vue'
    import { MdEditor, config, type ToolbarNames } from 'md-editor-v3'
    import 'md-editor-v3/lib/style.css'
    import { useTheme } from 'composables'

    const noStyle = { light: '', dark: '' }

    config({
        editorExtensions: {
            highlight: {
                css: {
                    a11y: noStyle,
                    atom: noStyle,
                    github: noStyle,
                    gradient: noStyle,
                    kimbie: noStyle,
                    paraiso: noStyle,
                    qtcreator: noStyle,
                    stackoverflow: noStyle,
                },
            },
        },
    })

    const props = withDefaults(
        defineProps<{
            modelValue?: string
            label?: string
            placeholder?: string
            readonly?: boolean
            language?: 'en-US' | 'it-IT' | 'es-ES' | 'fr-FR' | 'de-DE'
            height?: string
            minHeight?: string
            maxHeight?: string
            preview?: boolean
            toolbars?: ToolbarNames[]
            showCodeRowNumber?: boolean
            autoFocus?: boolean
            previewTheme?:
                | 'default'
                | 'github'
                | 'vuepress'
                | 'mk-cute'
                | 'smart-blue'
                | 'cyanosis'
            codeTheme?:
                | 'atom'
                | 'a11y'
                | 'github'
                | 'gradient'
                | 'kimbie'
                | 'paraiso'
                | 'qtcreator'
                | 'stackoverflow'
        }>(),
        {
            modelValue: '',
            label: '',
            placeholder: '',
            readonly: false,
            language: 'en-US',
            height: undefined,
            minHeight: '400px',
            maxHeight: undefined,
            preview: true,
            toolbars: () => [
                'bold',
                'underline',
                'italic',
                '-',
                'title',
                'strikeThrough',
                'sub',
                'sup',
                'quote',
                'unorderedList',
                'orderedList',
                'task',
                '-',
                'codeRow',
                'code',
                'link',
                // 'image',
                'table',
                'mermaid',
                'katex',
                '-',
                'revoke',
                'next',
                // 'save',
                '=',
                'pageFullscreen',
                // 'fullscreen',
                'preview',
                'previewOnly',
                // 'htmlPreview',
                // 'catalog',
                // 'github',
            ],
            showCodeRowNumber: true,
            autoFocus: false,
            previewTheme: 'github',
            codeTheme: 'github',
        },
    )

    const { isDarkTheme } = useTheme()

    const emit = defineEmits<{
        'update:modelValue': [value: string]
    }>()

    const localValue = ref(props.modelValue)

    watch(
        () => props.modelValue,
        (newValue) => {
            if (newValue !== localValue.value) {
                localValue.value = newValue
            }
        },
    )

    const handleChange = (value: string) => {
        localValue.value = value
        emit('update:modelValue', value)
    }

    const editorId = `md-editor-${Math.random().toString(36).slice(2, 11)}`
</script>

<template>
    <div class="pk-editor-markdown">
        <span v-if="label" class="pk-editor-markdown__label">
            {{ label }}
        </span>
        <div class="pk-editor-markdown__wrapper">
            <MdEditor
                :id="editorId"
                class="wysiwyg"
                :model-value="localValue"
                :placeholder
                :language
                :preview
                :toolbars
                :show-code-row-number
                :auto-focus
                :read-only="readonly"
                :theme="isDarkTheme ? 'dark' : 'light'"
                :preview-theme
                :style="{
                    height: height,
                    minHeight: minHeight,
                    maxHeight: maxHeight,
                }"
                @update:model-value="handleChange" />
        </div>
    </div>
</template>

<style lang="scss">
    @layer volver.components {
        .pk-editor-markdown {
            &:not(.mb-0) {
                margin-bottom: var(--input-gap);
            }

            &__label {
                display: var(--input-label-display);
                font-size: var(--input-label-font-size);
                font-weight: var(--input-label-font-weight);
                color: var(--input-label-color);
                display: var(--input-label-display);
                margin: 0 0 var(--input-label-gap) 0;
                white-space: var(--input-label-white-space);
                text-overflow: var(--input-label-text-overflow);
                overflow: var(--input-label-overflow);
                max-width: var(--input-label-max-width);
            }

            div.github-theme {
                h1,
                h2,
                h3,
                h4,
                h5,
                h6 {
                    margin-block-start: 1em;
                    margin-block-end: 0.5em;
                }
            }

            .md-editor {
                .cm-editor {
                    font-size: 12px;
                }

                &-preview {
                    font-size: 14px;
                }
            }
        }
    }
</style>
